import { useTranslation } from 'next-i18next';
import { PropsWithChildren, memo, useCallback } from 'react';
import { addCountryToSearchUrl } from '@bridebook/toolbox/src/add-country-to-search-url';
import { CountryCodes, Gazetteer, type Market } from '@bridebook/toolbox/src/gazetteer';
import type { Slug } from '@bridebook/toolbox/src/types';
import { useSearchSuppliersContext } from '@bridebook/web/app-basic/components/search-suppliers/search-suppliers.context';
import { getSupplierSearchCategoryUrl } from '@bridebook/web/app-shared/lib/search-suppliers/utils';
import { prettySupplierCategory } from '@bridebook/web/app-shared/lib/utils';
import CategorySection from './category-section';
import SearchSectionBlock from './search-section-block/search-section-block';
import SearchSuppliersContainer from '../search-suppliers-container';
import SearchSuppliersCarousel from './search-suppliers-carousel/search-suppliers-carousel';

interface IProps {
  isLoggedIn?: boolean;
  market: Market;
}

const SearchSuppliersCategories = ({ children, isLoggedIn, market }: PropsWithChildren<IProps>) => {
  const { t } = useTranslation('search');

  const buildItem = useCallback(
    (slug: Slug, location: string | { name: string; value: string }) => {
      const locationName = typeof location === 'string' ? location : location.name;
      const locationValue = typeof location === 'string' ? location : location.value;
      const { as: href } = getSupplierSearchCategoryUrl(slug, locationValue, market);

      return {
        href: addCountryToSearchUrl(href, market.country),
        title: locationName,
      };
    },
    [market],
  );

  const buildTypesItem = (slug: string, { name, value }: { name: string; value: string }) => {
    /**
     * Handle UK specifically so that existing links don't change.
     * We should NOT append location slug nor the actual country code, instead just "uk".
     *
     * @link https://bridebook.com/uk/search/wedding-venues/uk/castle
     */
    const { as: href } = getSupplierSearchCategoryUrl(
      slug,
      market.country === CountryCodes.GB ? market.prefix : market.getCountryName(),
      market,
    );

    return {
      href: `${addCountryToSearchUrl(href, market.country)}/${value}`,
      title: name,
    };
  };

  const buildRegionItem = (slug: string, { name, value }: { name: string; value: string }) => {
    const { as: href } = getSupplierSearchCategoryUrl(slug, value, market);

    return {
      href: addCountryToSearchUrl(href, market.country),
      title: name,
    };
  };
  const buildHeadingUrl = (slug: string, heading: string) => {
    const { as } = getSupplierSearchCategoryUrl(slug, heading, market);

    return addCountryToSearchUrl(as, market.country);
  };
  const { specific, generic, slug, topCountiesPerCountry, topTypes } = useSearchSuppliersContext();
  const categoryName = prettySupplierCategory({
    isLoggedIn: Boolean(isLoggedIn),
    addWedding: true,
    amount: 2,
    capitalize: true,
    slug,
    market,
  });
  const translationOpts = {
    category: categoryName,
  };

  const getNativeRegionName = useCallback(
    (region: string) => {
      if (region === Gazetteer.getCountryName(market.country)) {
        return market.getCountryName();
      }
      return region;
    },
    [market],
  );

  const cities = generic?.cities;

  const buildCarouselItem = useCallback(
    (location: string | { name: string; value: string }) => buildItem(slug, location),
    [buildItem, slug],
  );

  const buildTypesCarouselItem = useCallback(
    (location: string | { name: string; value: string }) =>
      buildTypesItem(slug, location as { name: string; value: string }),
    [buildTypesItem, slug],
  );

  return (
    <div data-name="search-suppliers-categories">
      <CategorySection>
        {Object.keys(topCountiesPerCountry ?? {}).map((region, index) => (
          <SearchSectionBlock
            {...(index === 0 && {
              section: t('suppliersCategories.byCounty', translationOpts),
              sectionClassName:
                '!mt-10 mb-4 border-b border-b-space-15 pb-4 text-space lg:mb-0 lg:text-[32px]',
            })}
            slug={slug}
            nativeRegionName={getNativeRegionName(region)}
            heading={t('suppliersCategories.heading', { region: getNativeRegionName(region) })}
            headingUrl={buildHeadingUrl(slug, region)}
            key={region}
            data={generic?.counties[region] || null}
            buildItem={buildItem}
            carousel={
              <SearchSuppliersCarousel
                buildItem={buildCarouselItem}
                items={topCountiesPerCountry?.[region] ?? []}
              />
            }
          />
        ))}
        {cities && (
          <SearchSectionBlock
            slug={slug}
            section={t('suppliersCategories.byCity', translationOpts)}
            key={`${categoryName}-by-City`}
            data={cities}
            buildItem={buildItem}
          />
        )}
      </CategorySection>
      <SearchSuppliersContainer>{children}</SearchSuppliersContainer>
      <CategorySection>
        {specific?.types && specific.types.length > 0 && (
          <SearchSectionBlock
            slug={slug}
            section={t('suppliersCategories.byType', translationOpts)}
            key={`${categoryName}-by-Type`}
            data={specific.types}
            sortByTitle={false}
            buildItem={buildTypesItem}
            carousel={
              <SearchSuppliersCarousel buildItem={buildTypesCarouselItem} items={topTypes || []} />
            }
          />
        )}
        {generic?.regions && (
          <SearchSectionBlock
            slug={slug}
            section={t('suppliersCategories.byRegion', translationOpts)}
            key={`${categoryName}-by-Region`}
            data={generic.regions}
            buildItem={buildRegionItem}
          />
        )}
      </CategorySection>
    </div>
  );
};

export default memo(SearchSuppliersCategories);
